
import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import BASE_URL from "../../BASEURL";

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    category: "",
    name: "",
    shortDesc: "",
    description: "",
    videoUrl: "",
    brochure: "",
    applications: [],
    features: [],
    image: ""
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imagePreview, setImagePreview] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        // Fetch categories
        const catRes = await fetch(`${BASE_URL}/api/category`);
        if (catRes.ok) {
          const catJson = await catRes.json();
          setCategories(catJson.data || []);
        }
        // Fetch product by ID
        const prodRes = await fetch(`${BASE_URL}/api/product/${id}`);
        if (!prodRes.ok) throw new Error("Product not found");
        const prodJson = await prodRes.json();
        const prod = prodJson.data;
        setFormData({
          category: prod.category || "",
          name: prod.name || "",
          shortDesc: prod.shortDesc || "",
          description: prod.description || "",
          videoUrl: prod.videoUrl || "",
          brochure: prod.brochure || "",
          applications: prod.applications || [],
          features: prod.features || [],
          image: prod.image || ""
        });
        setImagePreview(prod.image || "");
      } catch (err) {
        setError(err.message || "Failed to load product");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <p className="p-10">Loading...</p>;
  if (error) return <p className="p-10 text-red-600">{error}</p>;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleArrayChange = (index, field, value) => {
    const updated = [...formData[field]];
    updated[index] = value;
    setFormData({ ...formData, [field]: updated });
  };

  const addPoint = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const removePoint = (index, field) => {
    const updated = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updated });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, brochure: file });
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    try {
      const form = new FormData();
      form.append('category', formData.category);
      form.append('name', formData.name);
      form.append('shortDesc', formData.shortDesc);
      form.append('description', formData.description);
      form.append('videoUrl', formData.videoUrl);
      if (formData.brochure instanceof File) form.append('brochure', formData.brochure);
      if (formData.image instanceof File) form.append('image', formData.image);
      formData.applications.forEach((app, i) => form.append(`applications[${i}]`, app));
      formData.features.forEach((f, i) => form.append(`features[${i}]`, f));

      const res = await fetch(`${BASE_URL}/api/product/${id}`, {
        method: 'PUT',
        body: form,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || 'Update failed');
      }
      alert('Product updated!');
      navigate('/admin/productslist');
    } catch (err) {
      alert('Failed to update product');
    }
  };

  return (
    <section className="max-w-4xl mx-auto p-6 bg-white shadow rounded">
      <h1 className="text-2xl font-bold text-primaryDark mb-6">
        Edit Product: {formData.name}
      </h1>

      <div className="space-y-4">

        {/* IMAGE UPLOAD */}
        <div>
          <h3 className="font-semibold text-primaryDark mb-2">Product Image</h3>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="w-full border p-2 rounded"
          />
          {imagePreview && (
            <img
              src={imagePreview}
              alt="Preview"
              className="mt-3 h-40 object-contain border rounded p-2"
            />
          )}
        </div>

        <select
          name="category"
          value={formData.category}
          onChange={handleChange}
          className="w-full border p-2 rounded bg-white"
        >
          <option value="">Select Category</option>
          {categories.map((cat) => (
            <option key={cat._id || cat.id} value={cat._id || cat.id}>
              {cat.name || cat.title || cat.slug}
            </option>
          ))}
        </select>


        <input
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full border p-2 rounded"
          placeholder="Product Name"
        />

        <textarea
          name="shortDesc"
          value={formData.shortDesc}
          onChange={handleChange}
          className="w-full border p-2 rounded"
          placeholder="Short Description"
        />

        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full border p-2 rounded h-32"
          placeholder="Full Description"
        />



        {/* APPLICATIONS */}
        <div>
          <h3 className="font-semibold text-primaryDark mb-2">Applications</h3>
          {formData.applications.map((app, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input
                value={app}
                onChange={(e) => handleArrayChange(i, "applications", e.target.value)}
                className="w-full border p-2 rounded"
              />
              <button onClick={() => removePoint(i, "applications")} className="text-red-500">✕</button>
            </div>
          ))}
          <button onClick={() => addPoint("applications")} className="text-sm text-primary">+ Add Application</button>
        </div>

        {/* FEATURES */}
        <div>
          <h3 className="font-semibold text-primaryDark mb-2">Key Features</h3>
          {formData.features.map((f, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input
                value={f}
                onChange={(e) => handleArrayChange(i, "features", e.target.value)}
                className="w-full border p-2 rounded"
              />
              <button onClick={() => removePoint(i, "features")} className="text-red-500">✕</button>
            </div>
          ))}
          <button onClick={() => addPoint("features")} className="text-sm text-primary">+ Add Feature</button>
        </div>

        <input
          name="videoUrl"
          value={formData.videoUrl}
          onChange={handleChange}
          className="w-full border p-2 rounded"
          placeholder="YouTube Video URL"
        />

        {/* BROCHURE */}
        <div>
          <h3 className="font-semibold text-primaryDark mb-2">Brochure PDF</h3>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileUpload}
            className="w-full border p-2 rounded"
          />
          {formData.brochure && <p className="text-sm text-gray-600 mt-1">Current: {formData.brochure.name || formData.brochure}</p>}
        </div>

        <div className="flex gap-3 pt-4">
          <button
            onClick={handleSave}
            className="bg-primary text-white px-6 py-2 rounded hover:bg-primaryDark"
          >
            Save Changes
          </button>

          <button
            onClick={() => navigate("/admin/productslist")}
            className="border border-gray-400 px-6 py-2 rounded hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      </div>
    </section>
  );
}
